import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Grid,
  Link,
} from '@mui/material';
import {
  Close as CloseIcon,
  PictureAsPdf as PictureAsPdfIcon,
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
  LibraryBooks as LibraryBooksIcon,
  Article as ArticleIcon,
  Link as LinkIcon,
  Science as ScienceIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';

const MaterialCard = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  textDecoration: 'none',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}));

const IconWrapper = styled(Box)(({ theme, iconColor }) => ({
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.action.hover,
  flexShrink: 0,
  '& svg': {
    color: iconColor || theme.palette.primary.main,
  },
}));

const MaterialsModal = ({ open, onClose, resources }) => {
  const { t } = useTranslation(['lessons']);

  const getResourceIcon = (type) => {
    switch (type) {
      case 'PDF':
        return <PictureAsPdfIcon />;
      case 'Tutorial':
        return <SchoolIcon />;
      case 'Cheatsheet':
        return <MenuBookIcon />;
      case 'Guide':
        return <LibraryBooksIcon />;
      case 'Article':
        return <ArticleIcon />;
      case 'Link':
        return <LinkIcon />;
      case 'Jupyter Notebook':
        return <ScienceIcon />;
      default:
        return <DownloadIcon />;
    }
  };

  const getResourceColor = (type) => {
    switch (type) {
      case 'PDF':
        return '#e94435';
      case 'Tutorial':
        return '#4285f4';
      case 'Cheatsheet':
        return '#34a853';
      case 'Guide':
        return '#fbbc04';
      case 'Article':
        return '#ea4335';
      case 'Link':
        return '#4285f4';
      case 'Jupyter Notebook':
        return '#f9ab00';
      default:
        return '#757575';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" component="h2" fontWeight="bold">
            {t('lessonDetail.materials.title')}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {resources && resources.length > 0 ? (
          <Grid container spacing={2}>
            {resources.map((resource, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <MaterialCard
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconWrapper iconColor={getResourceColor(resource.type)}>
                    {getResourceIcon(resource.type)}
                  </IconWrapper>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {resource.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {resource.type}
                    </Typography>
                  </Box>
                </MaterialCard>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {t('lessonDetail.materials.noMaterials')}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MaterialsModal;
